import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { setPreviewLanguage } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";
import { RubricResetButton } from "../shared/RubricResetButton";

import { PropsType, ActionItem } from "./types";
import {
    DEFAULT_SECTION_TITLES,
    SECTION_ICONS,
    LANGUAGE_CODES,
    DEFAULT_ACTIONS,
} from "./constants";
import {
    CUSTOM_ICON_KEY,
    SCAN_TO_DEFAULT_MAIN_COLOR,
} from "../shared/constants";
import { useMultiCustomIcons } from "../shared/useMultiCustomIcons";
import {
    Container,
    LeftSection,
    SectionHeader,
    ActionsHeaderGroup,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
} from "./styled";
import { SectionTitleCard } from "./SectionTitleCard";
import { SectionIconCard } from "../shared/SectionIconCard";
import { ActionsTable } from "./ActionsTable";

// ─── Component ───────────────────────────────────────────

export function Actions({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const { setSharedActionsData } = React.useContext(ScanToContext);

    // ─── Languages (translated labels) ───────────────────
    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // ─── Section icons (translated labels) ───────────────
    const sectionIcons = React.useMemo(
        () =>
            SECTION_ICONS.map((i) => ({
                key: i.key,
                label: t(i.labelKey),
                path: i.path,
            })),
        [t]
    );

    // ─── Default section titles and actions from constants ─
    const defaultSectionTitles = DEFAULT_SECTION_TITLES;
    const defaultActions: ActionItem[] = DEFAULT_ACTIONS;

    // ─── State ───────────────────────────────────────────
    // Cast: ActionsConfig type doesn't declare every UI-state field used at
    // runtime (selectedLanguage, sectionTitles, etc.). Out of scope for ARCH-02.
    const savedActions = useSelector(
        (state: RootState) => state.scanTo?.actions
    ) as any;
    const [actionsEnabled, setActionsEnabled] = React.useState(
        savedActions?.actionsEnabled ?? true
    );
    const [sectionTitleVisible, setSectionTitleVisible] = React.useState(
        savedActions?.sectionTitleVisible ?? true
    );
    // Editing + preview share ONE language: the shared previewLanguage. Both
    // the section-title and action-buttons pickers drive it, so picking a
    // language here flips every section of the preview. Aliased so existing
    // call sites keep working unchanged.
    const dispatch = useDispatch();
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );
    const selectedLanguage = previewLanguage;
    const selectedButtonsLang = previewLanguage;
    const setSelectedLanguage = React.useCallback(
        (lang: string) => dispatch(setPreviewLanguage(lang)),
        [dispatch]
    );
    const setSelectedButtonsLang = setSelectedLanguage;
    const [selectedIcon, setSelectedIcon] = React.useState(
        savedActions?.selectedIcon || "tag"
    );
    // Multi-icon state lifted into the shared hook so all 4 tabs
    // (Offres / Actions / MaCommande / Publicites) share one source of
    // truth — see `shared/useMultiCustomIcons.ts`.
    const {
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        setSelectedCustomIdx,
        customIconUrl,
        iconFileInputRef,
        handleIconUpload,
        removeCustomIcon,
        resetCustomIcons,
    } = useMultiCustomIcons({
        saved: savedActions,
        fallbackIconKey: "tag",
        setSelectedIcon,
    });
    const [sectionTitles, setSectionTitles] = React.useState<
        Record<string, string>
    >(savedActions?.sectionTitles || { ...defaultSectionTitles });

    // Initial actions: prefer the persisted list, otherwise seed with the
    // 4 default actions (Commander / Voir ma commande / Payer / Appeler
    // serveur). Falling back to [] used to render the section as fully
    // disabled by default — the user had to hit "Réinitialiser" just to
    // get the baseline buttons back.
    const [actions, setActions] = React.useState<ActionItem[]>(
        savedActions?.fullActions ?? defaultActions
    );

    const [iconPickerOpen, setIconPickerOpen] = React.useState<number | null>(
        null
    );
    const iconPickerRef = React.useRef<HTMLDivElement>(null);

    const [
        broadcastChannel,
        setBroadcastChannel,
    ] = React.useState<BroadcastChannel | null>(null);

    // ─── Computed ────────────────────────────────────────
    const activeActions = React.useMemo(
        () =>
            [...actions]
                .filter((a) => a.active)
                .sort((a, b) => a.order - b.order),
        [actions]
    );

    // Auto-disable section if all actions are disabled
    const allActionsDisabled = React.useMemo(
        () => actions.every((a) => !a.active),
        [actions]
    );

    const effectiveEnabled = actionsEnabled && !allActionsDisabled;

    // ─── BroadcastChannel init (for external preview window only) ───
    React.useEffect(() => {
        const channel = new BroadcastChannel("scan-to-preview-channel");
        setBroadcastChannel(channel);
        return () => {
            channel.close();
        };
    }, []);

    // ─── Share actions data via context + BroadcastChannel ───
    React.useEffect(() => {
        const previewData = {
            source: "actions",
            actionsEnabled: effectiveEnabled,
            sectionTitleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            actions: activeActions.map((a) => ({
                id: a.id,
                icon: a.icon,
                iconColor: a.iconColor,
                title: a.title[selectedButtonsLang] || "",
                description: a.description[selectedButtonsLang] || "",
                titleVisible: a.titleVisible,
                descriptionVisible: a.descriptionVisible,
            })),
            mainColor,
            selectedLanguage,
            selectedButtonsLang,
            // Full config for Redux persistence
            sectionTitles,
            selectedIcon,
            customIconUrls,
            customIconFiles: customIconUrls.map((url, i) => ({
                filename: customIconFilenames[i] || "",
                url,
            })),
            selectedCustomIdx,
            fullActions: actions,
        };

        // Share with other same-page tabs via context
        setSharedActionsData(previewData);

        // Send to external preview window via BroadcastChannel
        if (broadcastChannel) {
            try {
                broadcastChannel.postMessage(previewData);
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem(
            "scanToActionsPreviewData",
            JSON.stringify(previewData)
        );
    }, [
        actionsEnabled,
        sectionTitleVisible,
        sectionTitles,
        selectedIcon,
        customIconUrl,
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        actions,
        selectedLanguage,
        selectedButtonsLang,
        mainColor,
        broadcastChannel,
        setSharedActionsData,
        activeActions,
        effectiveEnabled,
        sectionIcons,
    ]);

    // (Blob URL cleanup is now inside useMultiCustomIcons.)

    // ─── Close icon picker on outside click ──────────────
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                iconPickerRef.current &&
                !iconPickerRef.current.contains(e.target as Node)
            ) {
                setIconPickerOpen(null);
            }
        };
        if (iconPickerOpen !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [iconPickerOpen]);

    // ─── Handlers ────────────────────────────────────────

    const toggleActionActive = (actionId: number) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, active: !a.active } : a
            )
        );
    };

    const toggleActionTitleVisible = (actionId: number) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, titleVisible: !a.titleVisible } : a
            )
        );
    };

    const toggleActionDescVisible = (actionId: number) => {
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? { ...a, descriptionVisible: !a.descriptionVisible }
                    : a
            )
        );
    };

    const updateActionField = (
        actionId: number,
        field: "title" | "description",
        value: string
    ) => {
        setActions(
            actions.map((a) => {
                if (a.id !== actionId) return a;
                return {
                    ...a,
                    [field]: {
                        ...a[field],
                        [selectedButtonsLang]: value,
                    },
                };
            })
        );
    };

    const updateActionIcon = (actionId: number, iconKey: string) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, icon: iconKey } : a
            )
        );
        setIconPickerOpen(null);
    };

    const updateActionColor = (actionId: number, color: string) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, iconColor: color } : a
            )
        );
    };

    const resetSectionTitle = () => {
        setSectionTitles({
            ...sectionTitles,
            [selectedLanguage]: defaultSectionTitles[selectedLanguage],
        });
    };

    const resetAllSectionTitles = () => {
        setSectionTitles({ ...defaultSectionTitles });
        setSectionTitleVisible(true);
        setSelectedLanguage("fr");
    };

    const resetActionTitle = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          title: {
                              ...a.title,
                              [selectedButtonsLang]:
                                  def.title[selectedButtonsLang] || "",
                          },
                      }
                    : a
            )
        );
    };

    const resetActionDescription = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          description: {
                              ...a.description,
                              [selectedButtonsLang]:
                                  def.description[selectedButtonsLang] || "",
                          },
                      }
                    : a
            )
        );
    };

    // Reset one action's TEXTS (title + description, all languages) + visibility.
    const resetActionTexts = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          title: { ...def.title },
                          description: { ...def.description },
                          titleVisible: true,
                          descriptionVisible: true,
                      }
                    : a
            )
        );
    };

    // Reset EVERY item of one action to its default: icon, icon color,
    // title + description (all languages) and visibility.
    const resetAction = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          icon: def.icon,
                          iconColor: def.iconColor,
                          title: { ...def.title },
                          description: { ...def.description },
                          titleVisible: true,
                          descriptionVisible: true,
                      }
                    : a
            )
        );
    };

    const resetAllActions = () => {
        // Only reset action buttons (not section titles or icons)
        setActions(
            defaultActions.map((a) => ({
                ...a,
                title: { ...a.title },
                description: { ...a.description },
            }))
        );
        setSelectedButtonsLang("fr");
    };

    // Reset the WHOLE actions rubric to defaults, directly on local state (the
    // broadcast effect then syncs Redux + preview). Composes the section-title
    // and action sub-resets, plus the rubric toggle and the section icon.
    const resetActionsAll = () => {
        setActionsEnabled(true);
        setSelectedIcon("tag");
        resetCustomIcons();
        resetAllSectionTitles();
        resetAllActions();
    };

    // (handleIconUpload + removeCustomIcon are provided by useMultiCustomIcons.)

    // ─── JSX ─────────────────────────────────────────────
    return (
        <Container data-testid="scan-to-actions-container">
            {/* ─── Left: Configuration ─────────────────── */}
            <LeftSection>
                {/* Toggle activation + whole-rubric reset */}
                <ActionsHeaderGroup>
                    <SectionHeader>
                        <SectionTitle>{t("Actions")}</SectionTitle>
                        <ToggleContainer>
                            <ToggleLabel>{t("Disable actions")}</ToggleLabel>
                            <Toggle
                                data-testid="scan-to-actions-enabled-toggle"
                                isActive={actionsEnabled}
                                mainColor={mainColor}
                                onClick={() =>
                                    setActionsEnabled(!actionsEnabled)
                                }
                                aria-label={t("Disable actions")}
                            />
                        </ToggleContainer>
                    </SectionHeader>
                    <RubricResetButton
                        section="actions"
                        onReset={resetActionsAll}
                    />
                </ActionsHeaderGroup>

                {allActionsDisabled && actionsEnabled && (
                    <div
                        style={{
                            padding: "10px 16px",
                            backgroundColor: "#fff3e0",
                            borderRadius: 8,
                            fontSize: 13,
                            color: "#e65100",
                            border: "1px solid #ffe0b2",
                        }}
                    >
                        {t(
                            "All actions are disabled. The section will be hidden."
                        )}
                    </div>
                )}

                {/* Section title (multilingual) */}
                <SectionTitleCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    sectionTitles={sectionTitles}
                    defaultSectionTitles={defaultSectionTitles}
                    onSectionTitleChange={(lang, value) =>
                        setSectionTitles({
                            ...sectionTitles,
                            [lang]: value,
                        })
                    }
                    sectionTitleVisible={sectionTitleVisible}
                    onSectionTitleVisibleToggle={() =>
                        setSectionTitleVisible(!sectionTitleVisible)
                    }
                    onResetSectionTitle={resetSectionTitle}
                    onResetAllSectionTitles={resetAllSectionTitles}
                />

                {/* Section icon */}
                <SectionIconCard
                    mode="multi"
                    testIdPrefix="scan-to-actions"
                    mainColor={mainColor}
                    sectionIcons={sectionIcons}
                    selectedIcon={selectedIcon}
                    onIconSelect={setSelectedIcon}
                    customIconUrls={customIconUrls}
                    selectedCustomIdx={selectedCustomIdx}
                    onSelectCustomIcon={(idx) => {
                        setSelectedCustomIdx(idx);
                        setSelectedIcon(CUSTOM_ICON_KEY);
                    }}
                    iconFileInputRef={iconFileInputRef}
                    onIconUpload={handleIconUpload}
                    onRemoveCustomIcon={removeCustomIcon}
                />

                {/* Action cards configuration */}
                <ActionsTable
                    mainColor={mainColor}
                    languages={languages}
                    selectedButtonsLang={selectedButtonsLang}
                    onButtonsLangChange={setSelectedButtonsLang}
                    actions={actions}
                    defaultActions={defaultActions}
                    iconPickerOpen={iconPickerOpen}
                    iconPickerRef={iconPickerRef}
                    onSetIconPickerOpen={setIconPickerOpen}
                    onToggleActionActive={toggleActionActive}
                    onToggleActionTitleVisible={toggleActionTitleVisible}
                    onToggleActionDescVisible={toggleActionDescVisible}
                    onUpdateActionField={updateActionField}
                    onUpdateActionIcon={updateActionIcon}
                    onUpdateActionColor={updateActionColor}
                    onResetActionTitle={resetActionTitle}
                    onResetActionDescription={resetActionDescription}
                    onResetActionTexts={resetActionTexts}
                    onResetAction={resetAction}
                    onResetAllActions={resetAllActions}
                />
            </LeftSection>
        </Container>
    );
}
